import { css } from '@emotion/core'
import { useLocation } from '@reach/router'
import { graphql } from 'gatsby'
import React from 'react'

import Block from '../components/Block'
import Body from '../components/Body'
import { CardsNavigation } from '../components/CardsNavigation'
import CTAMain from '../components/ctas/CTAMain'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Header from '../components/Header'
import Layout from '../components/Layout'
import { LoadingSpinner } from '../components/LoadingComponent'
import LeftCornerPopup from '../components/popups/LeftCornerPopup'
import SearchBar, { searchBarHeight, SearchResultsBlock } from '../components/Search'
import SEO from '../components/SEO'
import { TemplateLinksNavigation } from '../components/TemplateLinksNavigation'
import { TemplatesIntro } from '../components/TemplatesIntro'
import TextCard from '../components/TextCard'
import YoutubeVideo from '../components/YoutubeVideo'
import { breakpoints } from '../constants/breakPoints'
import { templatesBlockBreakPoint, templatesHeaderBreakPoint, templatesLpBreakPoint } from '../constants/breakPoints'
import { isMobile } from '../helpers/common'
import { templatesTextCardFootnote } from '../helpers/templates'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { HeaderProps } from '../interfaces/header'
import { TemplatesPageProps } from '../interfaces/templates'
import { templatesHeading2Style } from '../pageTemplates/templates/Template'
import { templateTextCardStyle, youtubeVideoStyle } from '../pageTemplates/templates/TemplateCategory'

export const TemplatesHeader: React.FC<HeaderProps> = (props) => {
  return (
    <Header
      titleHref="/templates/"
      logoTitle="Templates"
      withDivider
      hideLogoTitleOnDesktop
      hideDemoButtonOnMobile
      customBreakPoint={templatesHeaderBreakPoint}
      withSearchOnSmallOnly
      searchPlaceholder="Search templates..."
      {...props}
    />
  )
}

// export for TemplateCategory.tsx (src/pageTemplates/templates/TemplateCategory.tsx)
export const featuredCategorySlugs = [
  'experts',
  'product-management',
  'roadmaps',
  'prioritization',
  'feedback-management',
  'multi-workspace',
  'technology',
]

const TemplatesPage: React.FC<TemplatesPageProps> = ({
  data: {
    templatesCategoriesData: { edges: templatesCategories },
    templatesData: { edges: templates },
  },
}) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const featuredCategories = templatesCategories.filter(({ node: { slug } }) =>
    featuredCategorySlugs.slice(0, 6).includes(slug)
  )

  const location = useLocation()

  // filter searchTerms by (debounced) search term
  const filteredSearchTerms = React.useMemo(
    () =>
      templates
        .filter(({ node: { name } }) => name.toLowerCase().includes(debouncedSearch.toLowerCase()))
        .sort((a, b) => a.node.name.localeCompare(b.node.name)),
    [debouncedSearch]
  )

  return (
    <Layout whiteBG>
      <SEO
        lang="en"
        title="Customizable Product Management Templates To Get You Started in Minutes"
        description="Roadmap templates - Prioritization templates - Product management templates | Fully adjustable and built on proven product management, product strategy, and roadmapping methods."
        imageSrc="templates.png"
        noindex={location.pathname.includes('/cta/')}
      />

      <TemplatesHeader search={search} setSearch={setSearch} />

      <Body>
        <div>
          {isMobile(templatesLpBreakPoint) && (
            <>
              {!isMobile(breakpoints.small) && (
                <SearchBar isSlidingComponent search={search} setSearch={setSearch} placeholder="Search templates..." />
              )}
              <CardsNavigation
                withSearch
                search={search}
                setSearch={setSearch}
                withBorderBottom
                stickyLayoutOnBreakPoint
                breakPoint={templatesLpBreakPoint}
                hrefPrefix="templates/category"
                categories={[
                  { node: { name: 'Featured', slug: 'featured', navigationIcon: {} } },
                  ...templatesCategories,
                  { node: { name: 'Submit your own', slug: 'submit-own-template', navigationIcon: {} } },
                ]}
                cssStyles={templatesCategoriesStyle}
              />
            </>
          )}

          <Block
            columnGap={2}
            columnsTemplate="2fr 6fr"
            alignContent="flex-start"
            breakPoint={templatesLpBreakPoint}
            css={contentContainerStyle}
          >
            {!isMobile(templatesLpBreakPoint) && (
              <TemplateLinksNavigation search={search} setSearch={setSearch} categories={templatesCategories} />
            )}
            <div css={categoriesContainerStyle}>
              <TemplatesIntro
                heading="Get started with ready-made templates"
                description="Find a template for inspiration, then make it your own. They're fully adjustable and built on proven product management and roadmapping methods."
                imageSrc="templates/lp-intro-image.webp"
              />
              <h2 className="heading2" css={heading2Style}>
                Featured templates
              </h2>
              <Block
                columns={3}
                alignContent="flex-start"
                twoColumnsForTablet
                rowGapMobile={2}
                css={templatesBlockStyle}
              >
                {templates
                  .filter(({ node: { featured } }) => featured)
                  .map(({ node: { abstract, name, workspacesCount, downloadableTemplateType, slug, visual } }) => (
                    <TextCard
                      key={`featured-template-${slug}`}
                      gatsbyImageSrc={visual}
                      title={name}
                      footnote={templatesTextCardFootnote(workspacesCount, downloadableTemplateType)}
                      description={abstract.abstract}
                      linkUrl={`/templates/${slug}/`}
                      cssStyles={templateTextCardStyle}
                    />
                  ))}
              </Block>

              <Divider size={6} />
              <h2 className="heading2 no-margin" css={templatesHeading2Style}>
                Learn how to use airfocus templates effectively
              </h2>
              <div css={youtubeVideoStyle}>
                <YoutubeVideo id="5ghpOynGuLo" className="gray-bordered-block" ratio={16 / 9} />
              </div>
            </div>
          </Block>

          <Divider size={8} />
        </div>

        <CardsNavigation
          categories={featuredCategories}
          heading="Featured categories"
          hrefPrefix="templates/category"
        />

        <CTAMain />

        {/* for mobile: Spinner is loaded from <Header.tsx /> */}
        {isMobile(templatesLpBreakPoint) && !isMobile(breakpoints.small) && search && <LoadingSpinner />}

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          useContainerOnly
          withSearchBar={!isMobile(templatesLpBreakPoint)}
          search={search}
          setSearch={setSearch}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchTerms}
          resultsBlockContent={
            <Block
              columns={4}
              alignContent="flex-start"
              breakPoint={templatesBlockBreakPoint}
              twoColumnsForTablet
              rowGapMobile={2}
              css={searchResultsBlockStyle}
            >
              {filteredSearchTerms.map(({ node: filteredTemplate }, index) => (
                <TextCard
                  key={`${debouncedSearch}-${filteredTemplate.slug}-${index}`}
                  gatsbyImageSrc={filteredTemplate.visual}
                  title={filteredTemplate.name}
                  footnote={templatesTextCardFootnote(
                    filteredTemplate.workspacesCount,
                    filteredTemplate.downloadableTemplateType
                  )}
                  description={filteredTemplate.abstract.abstract}
                  linkUrl={`/templates/${filteredTemplate.slug}/`}
                  cssStyles={templateTextCardStyle}
                />
              ))}
            </Block>
          }
        />

        <LeftCornerPopup
          title="Want to see the templates in action?"
          description="Book a demo and have all of your questions answered."
          withBookDemoButton
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default TemplatesPage

export const query = graphql`
  query {
    templatesCategoriesData: allContentfulProductTemplateCategory(
      filter: { slug: { ne: "others" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          navigationIcon {
            title
            gatsbyImageData(width: 60, height: 53, quality: 90)
          }
          order
          id
          slug
          name
        }
      }
    }

    templatesData: allContentfulProductTemplate(
      filter: { id: { ne: "15363eff-a7cb-5b42-b83d-8898b1bb543d" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          abstract {
            abstract
          }
          name
          workspacesCount
          downloadableTemplateType
          featured
          slug
          visual {
            gatsbyImageData(width: 600, quality: 90)
            title
          }
        }
      }
    }
  }
`

// export for submit-own-template.tsx
export const contentContainerStyle = css`
  margin: 4rem 0 8rem;

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    grid-template-columns: 249px auto;
  }

  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-top: 0 !important;
    margin-bottom: 4rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-bottom: 6rem;
  }
`

const templatesCategoriesStyle = css`
  a:first-child {
    color: #fff !important;
    background: #000 !important;
  }
`

const categoriesContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: start;
`

// export for TemplateCategory.tsx & submit-own-template.tsx
export const templatesBlockStyle = css`
  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media screen and (max-width: ${templatesBlockBreakPoint}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: initial;
  }
`

const heading2Style = css`
  margin-bottom: 2.5rem !important;
`

// export for TemplateCategory.tsx, Template.tsx & submit-own-template.tsx
export const searchResultsBlockStyle = css`
  margin-top: calc(${searchBarHeight}px + 3rem);

  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    margin-top: ${searchBarHeight}px;
  }

  @media screen and (max-width: ${templatesBlockBreakPoint}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: initial;
    margin-top: 0;
  }
`
